import {gql} from '@apollo/client';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getRegistrySupportedTypes, TagSchemaSummary} from 'constants/RegistryConstants';
import {Container} from 'constants/SampleConstants';
import SampleType, {SAMPLE_TYPE_DISPLAY} from 'constants/SampleType';
import {Bioentity} from 'folder-items/FileConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import BioentitySearchInput from 'inventory/BioentitySearchInput';
import preferences from 'Preferences';
import {RequestPreferences} from 'requests/RequestConstants';
import {SampleGroupSample} from 'samples/sample-groups/SampleGroupConstants';
import StorableSearchInput from 'samples/storables/StorableSearchInput';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {capitalize} from 'util/NameUtil';
import PickContentsFromContainerTable from 'workflows/stage-entries/components/PickContentsFromContainerTable';

import {
  SelectRequestSampleFormQuery,
  SelectRequestSampleFormQuery_tagSchema_TagSchemaNode as SelectRequestSampleFormTagSchema,
} from './__generated/SelectRequestSampleFormQuery';

export enum InputType {
  NON_CONTAINER_SAMPLE = 'NON_CONTAINER_SAMPLE',
  CONTAINER = 'CONTAINER',
}

const MODEL_TYPES = {
  [SampleType.BATCH]: [ModelTypeConstants.BATCH],
  [SampleType.BIOENTITY]: getRegistrySupportedTypes(),
};

interface ConnectedProps {
  // sampleSchema is null if either the sample schema is not restricted or it is a TagSchema and
  // the tag schema wasn't loaded yet.
  sampleSchema: SelectRequestSampleFormTagSchema | null | undefined;
}

export function getSampleSchemaId(props: SelectRequestSampleFormProps): string | null {
  if (props.sampleSchemaSummary == null) {
    return null;
  } else {
    assert(props.sampleType === SampleType.BIOENTITY, 'Sample type is expected to be BIOENTITY.');

    return props.sampleSchemaSummary?.id;
  }
}

const SELECT_REQUEST_SAMPLE_FORM_QUERY = gql`
  query SelectRequestSampleFormQuery($tagSchemaId: StrictString!) @skipAudit @engComponent(name: REQUESTS) {
    tagSchema(id: $tagSchemaId) {
      ... on TagSchemaNode {
        id
        isContainable
      }
      ...InaccessibleInfoGraphQL
    }
  }

  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function useSelectRequestSampleFormQuery(
  sampleSchemaId: string | null
): SelectRequestSampleFormTagSchema | null {
  const request = useSkippableBenchlingQuery<SelectRequestSampleFormQuery>(SELECT_REQUEST_SAMPLE_FORM_QUERY, {
    variables: {tagSchemaId: sampleSchemaId},
    skip: sampleSchemaId == null,
  });

  if (request.type === QueryResultType.SUCCESS && !isInaccessible(request.data.tagSchema)) {
    return request.data.tagSchema;
  }
  return null;
}

function QueryingSelectRequestSampleForm(props: SelectRequestSampleFormProps): JSX.Element {
  const sampleSchemaId = getSampleSchemaId(props);
  const sampleSchema = useSelectRequestSampleFormQuery(sampleSchemaId);

  return <SelectRequestSampleForm {...props} sampleSchema={sampleSchema} />;
}

interface SelectRequestSampleFormProps {
  autoFocus?: boolean;
  sampleSchemaSummary: TagSchemaSummary | null;
  sampleType: SampleType.BIOENTITY;
  onSelectedSampleChanged: (
    selectedSample: {sample: SampleGroupSample; container: Container | null} | null
  ) => void;
}

interface State {
  selectedInputType: InputType;
  sample: SampleGroupSample | null;
  samplesForContainer: Array<SampleGroupSample> | null;
  container: Container | null;
}

// TODO: replace with a better search component
export class SelectRequestSampleForm extends React.Component<
  SelectRequestSampleFormProps & ConnectedProps,
  State
> {
  static defaultProps = {
    autoFocus: true,
  };

  constructor(props: SelectRequestSampleFormProps & ConnectedProps) {
    super(props);

    let selectedInputType;
    if (this._isNonContainableEntitySchema()) {
      selectedInputType = InputType.NON_CONTAINER_SAMPLE;
    } else {
      selectedInputType =
        preferences.get(RequestPreferences.SELECT_REQUEST_SAMPLE_FORM_DEFAULT_INPUT_TYPE) ||
        InputType.NON_CONTAINER_SAMPLE;
    }

    this.state = {
      selectedInputType,
      sample: null,
      // samplesForContainer should either be a 0- or 1-element array since we pass shouldAllowMultiple=false
      // to PickContentsFromContainerTable. null is used as a sentinel to PickContentsFromContainerTable to
      // let it auto-select
      samplesForContainer: null,
      container: null,
    };
  }

  _isNonContainableEntitySchema(): boolean {
    if (this.props.sampleSchema == null) {
      return false;
    }
    return !this.props.sampleSchema.isContainable;
  }

  _handleSetSelectedInputType(selectedInputType: InputType): void {
    preferences.set(RequestPreferences.SELECT_REQUEST_SAMPLE_FORM_DEFAULT_INPUT_TYPE, selectedInputType);
    this.setState({
      selectedInputType,
      sample: null,
      samplesForContainer: null,
      container: null,
    });
    this._handleSampleChanged({
      sample: null,
      container: null,
      samplesForContainer: null,
    });
  }

  _renderInputTypeSelector(): JSX.Element | null {
    if (this._isNonContainableEntitySchema()) {
      return null;
    }
    return (
      <div>
        <label className="u-normal-font-weight">
          <input
            checked={this.state.selectedInputType === InputType.NON_CONTAINER_SAMPLE}
            onChange={() => {
              this._handleSetSelectedInputType(InputType.NON_CONTAINER_SAMPLE);
            }}
            type="radio"
          />
          <span className="u-tiny-spacing-left u-spacing-right">
            {capitalize(SAMPLE_TYPE_DISPLAY[this.props.sampleType])}
          </span>
        </label>
        <label className="u-normal-font-weight">
          <input
            checked={this.state.selectedInputType === InputType.CONTAINER}
            onChange={() => {
              this._handleSetSelectedInputType(InputType.CONTAINER);
            }}
            type="radio"
          />
          <span className="u-tiny-spacing-left u-spacing-right">Container</span>
        </label>
      </div>
    );
  }

  _handleSampleChanged({
    sample,
    container,
    samplesForContainer,
  }: {
    sample: SampleGroupSample | null;
    container: Container | null;
    samplesForContainer: Array<SampleGroupSample> | null;
  }): void {
    const isValid = sample || (container && samplesForContainer && samplesForContainer.length === 1);
    if (!isValid) {
      this.props.onSelectedSampleChanged(null);
    } else {
      this.props.onSelectedSampleChanged({
        sample: sample || samplesForContainer![0],
        container,
      });
    }
  }

  _handleSampleChange(inputType: InputType, inputs: Array<Container> | Array<SampleGroupSample>): void {
    assert(
      Object.values(InputType).includes(inputType),
      `Expected key ${inputType} to be one of ${Object.values(InputType)}.`
    );
    const input = inputs.length ? inputs[0] : null;

    const newState = {
      sample: this.state.sample,
      container: this.state.container,
      samplesForContainer: null,
    };
    if (inputType === InputType.CONTAINER) {
      newState.container = input as Container | null;
    } else {
      newState.sample = input as SampleGroupSample | null;
    }

    this.setState(newState);
    this._handleSampleChanged(newState);
  }

  _renderSearchInput(): JSX.Element {
    if (this.state.selectedInputType === InputType.NON_CONTAINER_SAMPLE) {
      const schemaId = this.props.sampleSchemaSummary ? this.props.sampleSchemaSummary.id : null;
      const onChange = (samples: Array<Bioentity>): void => {
        this._handleSampleChange(InputType.NON_CONTAINER_SAMPLE, samples);
      };
      const selected = this.state.sample ? [this.state.sample] : [];
      assert(
        this.props.sampleType === SampleType.BIOENTITY,
        `Unexpected sampleType "${this.props.sampleType}"`
      );
      return (
        <BioentitySearchInput
          autoFocus={this.props.autoFocus}
          onChange={onChange}
          selectedItems={selected}
          tagSchemaOrInterfaceId={schemaId}
        />
      );
    } else if (this.state.selectedInputType === InputType.CONTAINER) {
      return (
        <StorableSearchInput
          autoFocus={this.props.autoFocus}
          onChange={(containers: Array<Container>) => {
            this._handleSampleChange(InputType.CONTAINER, containers);
          }}
          registryId={null}
          selectedStorables={this.state.container ? [this.state.container] : []}
          storableTypes={[ModelTypeConstants.CONTAINER]}
        />
      );
    }
    throw new Error(`Unexpected input type "${this.state.selectedInputType}"`);
  }

  render(): JSX.Element {
    if (this.props.sampleSchemaSummary != null && this.props.sampleSchema == null) {
      return <CenteredHorizontalThrobber />;
    }
    return (
      <div>
        {this._renderInputTypeSelector()}
        <div className="u-small-spacing-top">{this._renderSearchInput()}</div>
        {this.state.container && (
          <PickContentsFromContainerTable
            containerId={this.state.container.id}
            shouldAllowMultiple={false}
            sampleSchema={this.props.sampleSchema}
            sampleTypes={MODEL_TYPES[this.props.sampleType]}
            selectedContainedItemIds={
              // eslint-disable-next-line no-restricted-syntax
              this.state.samplesForContainer ? _.pluck(this.state.samplesForContainer, 'id') : null
            }
            onSelectContainedItems={(samplesForContainer: Array<SampleGroupSample>) => {
              this.setState({samplesForContainer});
              this._handleSampleChanged({
                sample: this.state.sample,
                container: this.state.container,
                samplesForContainer,
              });
            }}
          />
        )}
      </div>
    );
  }
}

export default QueryingSelectRequestSampleForm;
